import React, { useState, useEffect, useMemo, useRef } from 'react';
import './QuestionScreen.css';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import ResultModal from '../ResultModal/ResultModal';

const QuestionScreen = ({ 
  questions, 
  currentQuestion, 
  timeLeft, 
  selectedAnswer, 
  handleAnswerSelect, 
  onGoHome,
  gameResult,
  showResult,
  nextQuestion,
  hideResultOverlay,
  answersDisabled
}) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  // Audio refs for sound effects
  const correctSoundRef = useRef(null);
  const wrongSoundRef = useRef(null);

  // Randomize options for the current question
  const randomizedOptions = useMemo(() => {
    if (!questions || !questions[currentQuestion]) return { options: [], correctIndex: 0, mapping: [] };
    
    const currentQ = questions[currentQuestion];
    const originalOptions = currentQ.options;
    const originalCorrect = currentQ.correct;
    
    console.log('=== OPTION RANDOMIZATION DEBUG ===');
    console.log('Original options:', originalOptions);
    console.log('Original correct index:', originalCorrect);
    
    // Create array of indices and shuffle them
    const indices = Array.from({ length: originalOptions.length }, (_, i) => i);
    console.log('Original indices:', indices);
    
    // Fisher-Yates shuffle algorithm with Math.random()
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    
    console.log('Shuffled indices:', indices);
    
    // Create shuffled options and find new correct index
    const shuffledOptions = indices.map(i => originalOptions[i]);
    const newCorrectIndex = indices.findIndex(i => i === originalCorrect);
    
    console.log('Shuffled options:', shuffledOptions);
    console.log('New correct index:', newCorrectIndex);
    console.log('=== END RANDOMIZATION DEBUG ===');
    
    return {
      options: shuffledOptions,
      correctIndex: newCorrectIndex,
      mapping: indices // Keep track of original indices for answer handling
    };
  }, [questions, currentQuestion]);

  // Handle answer selection with mapping back to original indices
  const handleShuffledAnswerSelect = (shuffledIndex) => {
    const originalIndex = randomizedOptions.mapping[shuffledIndex];
    handleAnswerSelect(originalIndex);
  };

  // Play sound effects and auto-hide result overlay
  useEffect(() => {
    if (showResult) {
      console.log('Result overlay shown, gameResult:', gameResult);
      
      // Play appropriate sound effect
      if (gameResult === 'correct') {
        console.log('Playing correct sound effect');
        if (correctSoundRef.current) {
          correctSoundRef.current.currentTime = 0; // Reset to start
          correctSoundRef.current.play().catch(error => {
            console.log('Failed to play correct sound:', error);
          });
        }
      } else if (gameResult === 'incorrect') {
        console.log('Playing incorrect sound effect');
        if (wrongSoundRef.current) {
          wrongSoundRef.current.currentTime = 0; // Reset to start
          wrongSoundRef.current.play().catch(error => {
            console.log('Failed to play wrong sound:', error);
          });
        }
      }
      // Note: No sound for 'timesUp' result
      
      const timer = setTimeout(() => {
        console.log('Auto-hiding result overlay after 2 seconds');
        
        // Check if this is the last question
        const isLastQuestion = currentQuestion === questions.length - 1;
        
        if (isLastQuestion) {
          console.log('Last question completed, going to game end screen');
          nextQuestion(); // This will trigger the game end
        } else {
          console.log('Not last question, hiding result overlay');
          hideResultOverlay();
        }
      }, 2000);

      // Cleanup timer if component unmounts or showResult changes
      return () => clearTimeout(timer);
    }
  }, [showResult, hideResultOverlay, gameResult, currentQuestion, questions.length, nextQuestion]);

  const handleHomeClick = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmHome = () => {
    setShowConfirmModal(false);
    onGoHome();
  };

  const handleCancelHome = () => {
    setShowConfirmModal(false);
  };

  return (
    <div className="screen question-screen">
      {/* Sound Effect Audio Elements */}
      <audio 
        ref={correctSoundRef}
        src="/sfx_correct.mp3"
        preload="auto"
      />
      <audio 
        ref={wrongSoundRef}
        src="/sfx_wrong.mp3"
        preload="auto"
      />
      
      <div className="game-background">
        <div className="question-frame">
          <div className="question-content">
            <div className="question-header">
              <div className="progress-section">
                <img src="/images/Icon_Book.png" alt="Progress" className="progress-icon" />
                <span className="progress-text">{currentQuestion + 1}/{questions.length}</span>
              </div>
              <div className="timer-section">
                <img src="/images/Icon_Stopwatch.png" alt="Timer" className="timer-icon" />
                <span className="timer-text">{timeLeft}s</span>
              </div>  
              <div className="home-section">
                <button className="home-btn" onClick={handleHomeClick}>
                  <img src="/images/btn_home.png" alt="Home" className="home-btn-image" />
                </button>
              </div>
            </div>
            
            <div className="question-container">
              <h2 className="question-text">{questions[currentQuestion].question}</h2>
            </div>

            {/* Next Question Button - shows after overlay is hidden (but not on last question) */}
            <div className={`next-question-container ${!showResult && answersDisabled && currentQuestion < questions.length - 1 ? 'visible' : 'hidden'}`}>
              <button className="next-question-btn" onClick={nextQuestion}>
                下一題<br/>Next
              </button>
            </div>
          </div>
        </div>
        <div className="answer-frame">
          <div className="options-container">
            {randomizedOptions.options.map((option, shuffledIndex) => {
              const originalIndex = randomizedOptions.mapping[shuffledIndex];
              const isSelected = selectedAnswer === originalIndex;
              const isCorrect = shuffledIndex === randomizedOptions.correctIndex;
              const isSelectedWrong = isSelected && !isCorrect;
              
              return (
                <button
                  key={shuffledIndex}
                  className={`option-button ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleShuffledAnswerSelect(shuffledIndex)}
                  disabled={answersDisabled}
                >
                  <span className="option-text">{option}</span>
                  
                  {/* Show tick icon on correct answer when answers are disabled */}
                  {answersDisabled && isCorrect && (
                    <img 
                      src="/images/icon_tick.png" 
                      alt="Correct" 
                      className="result-icon tick-icon"
                    />
                  )}
                  
                  {/* Show cross icon on selected wrong answer when answers are disabled */}
                  {/* {answersDisabled && isSelectedWrong && (
                    <img 
                      src="/images/icon_cross.png" 
                      alt="Wrong" 
                      className="result-icon cross-icon"
                    />
                  )} */}
                </button>
              );
            })}
        </div>
        </div>
      </div>

      {/* Result Modal Component */}
      <ResultModal 
        showResult={showResult}
        gameResult={gameResult}
      />

      <ConfirmModal
        isVisible={showConfirmModal}
        title={`要重新開始遊戲?
Restart the game?`}
        onConfirm={handleConfirmHome}
        onCancel={handleCancelHome}
      />
    </div>
  );
};

export default QuestionScreen; 